import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  critical: { label: 'Vencido', class: 'bg-red-100 text-red-800 border border-red-200', row: 'bg-red-50' },
  warning: { label: 'Próximo', class: 'bg-yellow-100 text-yellow-800 border border-yellow-200', row: 'bg-yellow-50' },
  ok: { label: 'OK', class: 'bg-green-100 text-green-700', row: '' },
};

export default function MantenimientoPreventivo() {
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/maintenance/plan')
      .then(r => setPlan(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = plan.filter(p => {
    const matchStatus = filter === 'all' || p.status === filter;
    const matchType = typeFilter === 'all' || p.equipment_type === typeFilter;
    return matchStatus && matchType;
  });

  const counts = {
    critical: plan.filter(p => p.status === 'critical').length,
    warning: plan.filter(p => p.status === 'warning').length,
    ok: plan.filter(p => p.status === 'ok').length,
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Plan de Mantenimiento Preventivo</h1>
        <p className="text-sm text-gray-500 mt-0.5">Estado de todos los mantenimientos programados</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('critical')}>
          <AlertTriangle size={20} className="text-red-600" />
          <div>
            <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{counts.critical}</p>
            <p className="text-xs text-red-600">Vencidos</p>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('warning')}>
          <Clock size={20} className="text-yellow-600" />
          <div>
            <p className="text-2xl font-bold text-yellow-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{counts.warning}</p>
            <p className="text-xs text-yellow-600">Próximos (&lt;50h)</p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('ok')}>
          <CheckCircle size={20} className="text-green-600" />
          <div>
            <p className="text-2xl font-bold text-green-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{counts.ok}</p>
            <p className="text-xs text-green-600">Al día</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { key: 'all', label: 'Todos' },
          { key: 'critical', label: 'Vencidos' },
          { key: 'warning', label: 'Próximos' },
          { key: 'ok', label: 'Al día' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
              ${filter === f.key ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
        <div className="h-px w-px mx-1" />
        {[
          { key: 'all', label: 'Móvil + Fija' },
          { key: 'mobile', label: 'Solo Móvil' },
          { key: 'fixed', label: 'Solo Fija' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setTypeFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
              ${typeFilter === f.key ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Estado</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Equipo</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Tipo Mantenimiento</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">H Actuales</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Próximo a</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">H Restantes</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase w-48">Descripción</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Ver</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">Sin resultados</td></tr>
            ) : (
              filtered.map((item, i) => {
                const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.ok;
                return (
                  <tr key={i} className={`table-row-hover ${cfg.row}`} data-testid={`plan-row-${i}`}>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.class}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-900 text-xs">{item.equipment_name}</p>
                      <p className="text-[10px] text-gray-400">{item.equipment_type === 'mobile' ? 'Móvil' : 'Fija'} · {item.section || item.equipment_code}</p>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700">{item.maintenance_type}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">{item.current_hours}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">{item.next_maintenance_hours} h</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-mono text-xs font-bold ${item.status === 'critical' ? 'text-red-600' : item.status === 'warning' ? 'text-yellow-600' : 'text-gray-600'}`}>
                        {item.hours_until_maintenance <= 0 ? 'VENCIDO' : `${item.hours_until_maintenance.toFixed(0)} h`}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[180px] truncate">{item.description}</td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => navigate(`/equipos/${item.equipment_id}`)}
                        className="p-1 text-gray-400 hover:text-[#16a34a] rounded transition-colors"
                        data-testid={`plan-view-${i}`}
                      >
                        <ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
